import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Heart, Brain, Sparkles, Sun, Cloud, CloudRain, CloudSun } from "lucide-react";
import { useAuth } from "./AuthContext";
import { saveSymptoms } from "./partnerApi";

const MOODS = [
  { value: 5, label: "Great", emoji: "😊", icon: Sun, color: "from-amber-400 to-orange-400" },
  { value: 4, label: "Good", emoji: "🙂", icon: CloudSun, color: "from-yellow-400 to-amber-400" },
  { value: 3, label: "Okay", emoji: "😐", icon: Cloud, color: "from-slate-400 to-slate-500" },
  { value: 2, label: "Low", emoji: "😔", icon: CloudRain, color: "from-blue-400 to-slate-500" },
  { value: 1, label: "Struggling", emoji: "😢", icon: CloudRain, color: "from-indigo-500 to-slate-600" }
];

const ANXIETY_LEVELS = [
  { value: 1, label: "Calm", emoji: "🧘‍♀️" },
  { value: 2, label: "Mild", emoji: "😌" },
  { value: 3, label: "Moderate", emoji: "😟" },
  { value: 4, label: "High", emoji: "😰" },
  { value: 5, label: "Severe", emoji: "😵‍💫" }
];

const QUICK_SYMPTOMS = [
  "Fatigue", "Bloating", "Headache", "Cramps", "Nausea",
  "Anxiety", "Brain fog", "Sleep issues", "Mood swings", "Cravings"
];

export default function MoodCheck({ onClose, existingEntry, onSave }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [mood, setMood] = useState(existingEntry?.mood || null);
  const [anxiety, setAnxiety] = useState(existingEntry?.anxiety || null);
  const [symptoms, setSymptoms] = useState(existingEntry?.symptoms || []);
  const [notes, setNotes] = useState(existingEntry?.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    try {
      await saveSymptoms(user.id, new Date().toISOString().split("T")[0], {
        mood,
        anxiety,
        symptoms,
        notes
      });
      
      if (onSave) onSave({ mood, anxiety, symptoms, notes });
      onClose();
    } catch (err) {
      console.error("Error saving mood check:", err);
    }
    setSaving(false);
  };

  const toggleSymptom = (symptom) => {
    setSymptoms(prev => 
      prev.includes(symptom) 
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const getEncouragement = () => {
    if (mood <= 2 || anxiety >= 4) {
      return "I hear you. It's okay to not be okay. Remember: you're not alone in this. 💙";
    }
    if (mood >= 4 && anxiety <= 2) {
      return "That's wonderful! Keep nurturing yourself. You're doing amazing! ✨";
    }
    return "Thank you for checking in. Every day you track is a step forward. 💕";
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Daily Check-In</h2>
                <p className="text-rose-100 text-sm">How are you feeling today?</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white transition">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          {/* Progress dots */}
          <div className="flex gap-2 mt-4 justify-center">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition ${
                  s <= step ? "bg-white" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Mood */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  How's your mood?
                </h3>
                
                <div className="grid grid-cols-5 gap-2">
                  {MOODS.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setMood(m.value)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        mood === m.value
                          ? `border-transparent bg-gradient-to-br ${m.color} text-white shadow-lg scale-105`
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-2xl block">{m.emoji}</span>
                      <span className={`text-xs mt-1 block ${mood === m.value ? "text-white" : "text-slate-600"}`}>
                        {m.label}
                      </span>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setStep(2)}
                  disabled={!mood}
                  className="w-full mt-6 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition"
                >
                  Continue
                </button>
              </motion.div>
            )}

            {/* Step 2: Anxiety */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-500" />
                  Anxiety level today?
                </h3>
                
                <div className="space-y-2">
                  {ANXIETY_LEVELS.map((a) => (
                    <button
                      key={a.value}
                      onClick={() => setAnxiety(a.value)}
                      className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                        anxiety === a.value
                          ? "border-purple-500 bg-purple-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span className="text-2xl">{a.emoji}</span>
                      <span className={`font-medium ${anxiety === a.value ? "text-purple-700" : "text-slate-700"}`}>
                        {a.label}
                      </span>
                      {anxiety === a.value && (
                        <div className="ml-auto w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 bg-slate-100 text-slate-700 rounded-xl py-3 font-medium hover:bg-slate-200 transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!anxiety}
                    className="flex-1 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition"
                  >
                    Continue
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Symptoms & Notes */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  Any symptoms today?
                </h3>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {QUICK_SYMPTOMS.map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleSymptom(s)}
                      className={`px-3 py-1.5 rounded-full text-sm transition ${
                        symptoms.includes(s)
                          ? "bg-rose-500 text-white"
                          : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  Anything else? (optional)
                </h3>
                
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="How are you really feeling? What's on your mind?"
                  className="w-full p-4 border border-slate-200 rounded-xl resize-none h-24 focus:ring-2 focus:ring-rose-300 focus:border-rose-300 outline-none"
                />

                {/* Encouragement */}
                <div className="mt-4 p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100">
                  <p className="text-slate-700 text-sm text-center">{getEncouragement()}</p>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 bg-slate-100 text-slate-700 rounded-xl py-3 font-medium hover:bg-slate-200 transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl py-3 font-medium hover:shadow-lg transition disabled:opacity-50"
                  >
                    {saving ? "Saving..." : "Save Check-In ✓"}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}